import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getGreeting } from './getGreeting';
import { useLocation } from 'react-router-dom';

import NewInspections from './newInspections/NewInspections';
import MyInspections from './myInspections/MyInspections';
import CustomTabs from '../../components/tabs/CustomTabs';
import { getTaskCount } from './getData';
import { usePermissions } from '../../hooks/usePermissions';
import { PermissionEnum } from '../../enums/PermissionEnum';
import MyPrices from './myPrices/MyPrices';
import dayjs from 'dayjs';
import { analysisService } from '../../api/services/analysisService';
import CitizenReportedInspectionList from './citizenReportedPrices/CitizenReportedInspectionList';

const InspectorPortal = () => {
  const { t } = useTranslation(['inspector']);
  const location = useLocation();

  const { hasPermission } = usePermissions();

  const startOfWeek = dayjs().startOf('week');
  const endOfWeek = dayjs().endOf('week');

  const greeting = getGreeting(t);
  const userName = localStorage.getItem('name');
  const [weeklyCompliance, setWeeklyCompliance] = useState<number>(0);

  const [taskCount, setTaskCount] = useState(0);

  const tabs = useMemo(() => {
    const baseItems = [
      {
        key: '1',
        label: t('overview'),
        children: <NewInspections />,
      },
      {
        key: '2',
        label: t('inspections'),
        children: <MyInspections />,
      },
      {
        key: '3',
        label: t('prices'),
        children: (
          <>
            <MyPrices triggeredFrom="inspectorPortal" />
          </>
        ),
      },
    ];

    if (hasPermission(PermissionEnum.VIEW_PRICE_REQUESTS)) {
      return [
        ...baseItems,
        {
          key: '4',
          label: t('citizenReportedPrices'),
          children: (
            <>
              <CitizenReportedInspectionList />
            </>
          ),
        },
      ];
    }

    return baseItems;
  }, [t, hasPermission]);

  useEffect(() => {
    const getTaskCountWrapper = async () => {
      try {
        const data = await getTaskCount();
        setTaskCount(data);
      } catch (error) {
        console.error('Error in fetching task count:', error);
      }
    };

    getTaskCountWrapper();
  }, []);

  useEffect(() => {
    const fetchPerformanceData = async () => {
      try {
        const data = await analysisService.getDetailedPerformance({
          startDate: startOfWeek.valueOf(),
          endDate: endOfWeek.valueOf(),
        });
        if (data?.tasks?.completionRate) {
          setWeeklyCompliance(data?.tasks?.completionRate);
        }
        // setPerformanceData(data);
      } catch (err) {
        console.error('Error fetching performance data:', err);
      } finally {
      }
    };

    fetchPerformanceData();
  }, []);

  return (
    <>
      <section className="mb-4 mt-12 flex w-full flex-row items-start justify-between">
        <div>
          <h3 className="text-[32px] text-headings-font-color sm:text-[64px] md:text-[48px]">
            {greeting}, {userName}
          </h3>
          <p className="text-base font-medium text-font-color">
            {hasPermission([PermissionEnum.RUN_INSPECTIONS]) &&
              taskCount !== undefined &&
              t('taskCount', { count: taskCount })}
          </p>
        </div>

        <div className="mt-5 self-end text-right sm:mt-0">
          <h3 className="text-xl font-bold text-green-600 md:text-2xl">{weeklyCompliance}%</h3>
          <p className="text-xs text-font-color md:text-sm">{t('weeklyCompliance')}</p>
        </div>
      </section>

      <section className="my-6 w-full">
        <CustomTabs
          items={tabs}
          defaultActiveKey={location.state?.activeTab || '1'}
          className="inspector-tabs"
        />
      </section>

      {/* {tabItems[selectedTab].children} */}
    </>
  );
};

export default InspectorPortal;
